import { useFormikSubmit } from "@certn/api";
import { useClientPortalAPI } from "@certn/api/src/useClientPortalAPI";
import { showToast } from "@certn/cascadia";
import {
  Card,
  Container,
  Divider,
  SelectFormikField,
  SelectItem,
  TextField,
} from "@certn/cascadia-react";
import { BasicButton, PrimaryButton, Stack } from "@certn/cascadia";
import { getRegionKey } from "@certn/utils";
import { css } from "@emotion/css";
import { Form, Formik, FormikHelpers } from "formik";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { object, string } from "yup";

import { components } from "@certn/api/generated-schemas/client-portal";
import { ReportUrlTypeField } from "../../../components/ReportUrlTypeField";
import { AtsIntegration } from "../../WorkableIntegrationContext";

interface Props {
  initialValues: components["schemas"]["AtsIntegrationDetail"];
  reload?: () => void;
}

export function WorkableEditStep({ initialValues, reload }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // AU (APAC) only permits the authenticated Client Portal URL for Workable.
  const isAuRegion = getRegionKey() === "au";

  const integrationData = {
    integration_id: initialValues.workable_integration?.id || "",
    api_key: initialValues.workable_integration?.api_key || "",
    // Seed the restricted value directly in AU so the disabled PDF option is
    // never the initial selection (avoids a flicker and a dirtied read-only form).
    report_url_type: isAuRegion
      ? "CLIENT_PORTAL_URL"
      : initialValues.report_url_type || "CLIENT_PORTAL_URL",
    customer_group_id:
      initialValues.workable_integration?.customer_group_id || "",
  };

  const { data: customerGroups, isLoading: isLoadingCustomerGroups } =
    useClientPortalAPI(
      "/api/internal/client-portal/ats-integrations/customer-groups/"
    );

  const validationSchema = object().shape({
    customer_group_id: string().required().label(t("Default group")),
  });

  const { submit: updateIntegration, isSubmitting } = useFormikSubmit<
    components["schemas"]["AtsIntegrationDetail"]
  >(
    `/api/internal/client-portal/ats-integrations/${initialValues.id}/update/`,
    {
      onSuccess: () => {
        showToast(t("Integration updated successfully"), {
          type: "success",
        });
        reload?.();
        navigate("/settings/ats-integrations");
      },
      showToastOnError: true,
    }
  );

  const handleSubmit = useCallback(
    async (
      values: AtsIntegration,
      formikHelpers: FormikHelpers<AtsIntegration>
    ) => {
      await updateIntegration(
        {
          report_url_type: values.report_url_type || "CLIENT_PORTAL_URL",
          lever_integration: null,
          workday_integration: null,
          workable_integration: {
            customer_group_id: values.customer_group_id || "",
          },
        },
        formikHelpers
      );
    },
    [updateIntegration]
  );

  const shouldDisableForm = isSubmitting || !initialValues.is_active;

  return (
    <Formik
      initialValues={integrationData}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({ isValid, values }) => (
        <Container
          layout="narrow"
          testID="workable-setup-page-outermost-container"
          className={styles.container}
        >
          <Form>
            <Card type="large" className={styles.card}>
              <Stack gap="space-4">
                <div className={styles.mainContentContainer}>
                  <section>
                    <TextField
                      className={styles.textFieldSpacing}
                      label={t("API key")}
                      name="api_key"
                      testID="workable-api-key"
                      isDisabled
                      isRequired
                      value={values.api_key}
                    />
                  </section>

                  <ReportUrlTypeField
                    isDisabled={shouldDisableForm}
                    defaultValue="CLIENT_PORTAL_URL"
                    isPdfUrlDisabled={isAuRegion}
                    note={
                      isAuRegion
                        ? t(
                            "Only the Client Portal URL (Authenticated) option is available in the APAC region."
                          )
                        : undefined
                    }
                  />

                  <Divider />

                  <section>
                    <SelectFormikField
                      label={t("Select a group")}
                      testID="workable-customer-group"
                      name="customer_group_id"
                      isRequired
                      isLoading={isLoadingCustomerGroups}
                      isDisabled={shouldDisableForm}
                    >
                      {customerGroups?.map(({ id, name }) => (
                        <SelectItem key={id} id={id} textValue={name}>
                          {name}
                        </SelectItem>
                      ))}
                    </SelectFormikField>
                  </section>

                  <Stack orientation="horizontal" justify="end" gap="space-4">
                    <BasicButton
                      testID="cancel-workable"
                      type="button"
                      isDisabled={isSubmitting}
                      onPress={() => navigate("/settings/ats-integrations")}
                    >
                      {t("Cancel")}
                    </BasicButton>
                    <PrimaryButton
                      testID="save-workable"
                      type="submit"
                      isDisabled={
                        isSubmitting || !isValid || !initialValues.is_active
                      }
                    >
                      {t("Save changes")}
                    </PrimaryButton>
                  </Stack>
                </div>
              </Stack>
            </Card>
          </Form>
        </Container>
      )}
    </Formik>
  );
}

const styles = {
  mainContentContainer: css({
    display: "flex",
    gap: "var(--space-5)",
    flexDirection: "column",
  }),
  container: css({
    rowGap: "1rem !important",
  }),
  card: css({
    marginTop: "var(--space-4)",
    padding: "var(--space-4)",
    gap: "var(--space-4)",
  }),
  textFieldSpacing: css({ display: "flex", flex: "1" }),
};
